// import * as Colors from '@brightlayer-ui/colors';
import { useRegistrationContext, useRegistrationWorkflowContext } from '@brightlayer-ui/react-auth-workflow';
import { ErrorManagerProps } from '@brightlayer-ui/react-auth-workflow/dist/components/Error/types';
import { useErrorManager } from '@brightlayer-ui/react-auth-workflow/dist/contexts/ErrorContext/useErrorManager';
import { AxiosError, HttpStatusCode } from 'axios';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { UserService } from '../../../services/user/user.service';
import { CreateAccountPageBase } from './CreateAccountPageBase';
import { CreateAccountPageProps } from './types';
/**
 * Component that renders a screen for the user to enter their email address
 * to start the account creation process.
 *
 * This component has been ported over from BLUI React Workflows and modified.
 *
 * @param {CreateAccountPageProps} props - props of CreateAccountScreen
 *
 * @category Component
 */

const EMAIL_REGEX = /^[A-Z0-9._%+'-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

export const CreateAccountPage: React.FC<CreateAccountPageProps> = (props) => {
    const { t } = useTranslation();
    const { actions } = useRegistrationContext();
    const regWorkflow = useRegistrationWorkflowContext();
    const { nextScreen, previousScreen, screenData, totalScreens, currentScreen } = regWorkflow;

    const {
        WorkflowCardBaseProps,
        WorkflowCardHeaderProps,
        WorkflowCardInstructionProps,
        WorkflowCardActionsProps,
        emailLabel = t('bluiCommon:LABELS.EMAIL'),
        emailInitialValue = screenData.CreateAccount.emailAddress,
        emailValidator = (email: string): boolean | string => {
            if (!EMAIL_REGEX.test(email)) {
                return t('bluiCommon:MESSAGES.EMAIL_ENTRY_ERROR');
            }
            return true;
        },
        emailTextFieldProps,
        setHasEatonAccount,
        ...otherProps
    } = props;

    const [emailInputValue, setEmailInputValue] = useState(screenData.CreateAccount.emailAddress);
    const [isLoading, setIsLoading] = useState(false);
    const { triggerError, errorManagerConfig } = useErrorManager();

    const errorDisplayConfig: ErrorManagerProps = {
        ...errorManagerConfig,
        ...props.errorDisplayConfig,
        onClose: (): void => {
            props.errorDisplayConfig?.onClose?.();
            if (errorManagerConfig.onClose) errorManagerConfig?.onClose();
        },
    };

    const onNext = useCallback(async () => {
        try {
            setIsLoading(true);
            const { hasEatonAccount } = await UserService.validateRegistration({
                email: emailInputValue,
            });

            if (setHasEatonAccount) {
                setHasEatonAccount(hasEatonAccount);
            }

            void nextScreen({
                screenId: 'CreateAccount',
                values: { emailAddress: emailInputValue },
            });
        } catch (error) {
            if (error instanceof AxiosError && error.response?.status === HttpStatusCode.Conflict) {
                triggerError(new Error(t('CREATE_ACCOUNT_PAGE.EMAIL_ALREADY_EXISTS_ERROR')));
            } else {
                triggerError(error as Error);
            }
        } finally {
            setIsLoading(false);
        }
    }, [actions, emailInputValue, nextScreen, triggerError]);

    const onPrevious = (): void => {
        previousScreen({
            screenId: 'CreateAccount',
            values: { emailAddress: emailInputValue },
        });
    };

    const workflowCardBaseProps = {
        loading: isLoading,
        ...WorkflowCardBaseProps,
    };

    const workflowCardHeaderProps = {
        title: t('bluiRegistration:REGISTRATION.STEPS.CREATE_ACCOUNT'),
        ...WorkflowCardHeaderProps,
    };

    const workflowCardInstructionProps = {
        instructions: t('bluiRegistration:SELF_REGISTRATION.INSTRUCTIONS'),
        ...WorkflowCardInstructionProps,
    };

    const workflowCardActionsProps = {
        showNext: true,
        nextLabel: t('bluiCommon:ACTIONS.NEXT'),
        showPrevious: true,
        previousLabel: t('bluiCommon:ACTIONS.BACK'),
        canGoPrevious: true,
        currentStep: currentScreen,
        totalSteps: totalScreens,
        ...WorkflowCardActionsProps,
        onNext: (): void => {
            void onNext();
            WorkflowCardActionsProps?.onNext?.();
        },
        onPrevious: (): void => {
            void onPrevious();
            WorkflowCardActionsProps?.onPrevious?.();
        },
    };

    const onEmailInputValueChange = (e: any): void => {
        setEmailInputValue(e.target.value);
    };

    return (
        <CreateAccountPageBase
            WorkflowCardBaseProps={workflowCardBaseProps}
            WorkflowCardHeaderProps={workflowCardHeaderProps}
            WorkflowCardInstructionProps={workflowCardInstructionProps}
            emailLabel={emailLabel}
            emailInitialValue={
                screenData.CreateAccount.emailAddress.length > 0
                    ? screenData.CreateAccount.emailAddress
                    : emailInitialValue
            }
            emailTextFieldProps={{ ...emailTextFieldProps, onChange: onEmailInputValueChange }}
            emailValidator={emailValidator}
            WorkflowCardActionsProps={workflowCardActionsProps}
            errorDisplayConfig={errorDisplayConfig}
            {...otherProps}
        />
    );
};
